/** Polygon caps are triangulated independently of contour order, but Babylon's
 * side-wall builder uses that order directly (and flips hole walls itself).
 * Canonicalize BOTH outer and hole loops to positive signed XZ area before
 * either stage. This is a profile-builder contract, not arbitrary mesh repair.
 */
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

export function polygonOptions(options: Record<string, unknown>): Record<string, unknown> {
    const normalize = (value: unknown, label: string): Vector3[] => {
        if (!Array.isArray(value) || value.length < 3) throw new Error(`${label} needs at least three Vector3 points in the XZ plane (y=0).`);
        const points = value.map((v: unknown) => {
            const p = v as { x?: number; y?: number; z?: number } | null;
            if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.z) || p.y !== 0) {
                throw new Error(`${label} points must be finite Vector3(x,0,z). Build in XZ, then rotate the finished mesh.`);
            }
            return new Vector3(p.x, 0, p.z);
        });
        if (points[0].equals(points[points.length - 1])) points.pop();
        if (points.length < 3) throw new Error(`${label} needs at least three distinct points.`);
        const origin = points[0];
        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const a = points[i], b = points[(i + 1) % points.length];
            area += (a.x-origin.x)*(b.z-origin.z)-(b.x-origin.x)*(a.z-origin.z);
        }
        if (!Number.isFinite(area) || area === 0) throw new Error(`${label} has zero or invalid signed area; supply a simple non-collinear loop.`);
        // Keep the first vertex stable for the native side-wall UV seam.
        return area < 0 ? [points[0], ...points.slice(1).reverse()] : points;
    };
    if (options.depth !== undefined && (typeof options.depth !== 'number' || !Number.isFinite(options.depth) || options.depth < 0)) {
        throw new Error('Polygon depth must be finite and nonnegative; positive depth runs from y=0 to y=-depth. Rotate/translate the resulting mesh to place it.');
    }
    if (options.holes !== undefined && !Array.isArray(options.holes)) throw new Error('Polygon holes must be an array of XZ loops.');
    return { ...options, shape: normalize(options.shape, 'Polygon shape'),
        holes: (options.holes as unknown[] | undefined)?.map((ring, i) => normalize(ring, `Polygon hole ${i}`)) ?? [] };
}
